import { Formik } from "formik";
import OrganizationFields from "../../formsFields/OrganizationFields";
import SignUpFlowValues from "../../../services/models/formModels/signUpFlowValues.model";
import { Link } from "react-router-dom";
import LogoSVG from "../../../assets/svg/Logo";
import { Fragment, useEffect, useState } from "react";
import validations from "../../../services/validations";
import Button from "../../organism/Button";
import PhysicianFields from "../../formsFields/PhysicianFields";
import DropDownIcon from "../../../assets/svg/DropdownIcon";
import OrganizationFieldsProps from "../../../services/models/formModels/organizationSignUpFields.model";
import OfficeManagerFields from "../../formsFields/OfficeManagerFields";
import dataConstants from "../../../constants/dataConstants";
import ErrorBoundary from "../ErrorBoundary";
import RoleSelection from "../RoleSelection";
import useSignUpUser from "../../../services/utils/custom hooks/SignUp/useSignUpUser";
import SignUpTestSelectionStep from "../SignUpTestSelectionStep";
import useStateAndCity from "../../../services/utils/custom hooks/useStateAndCity";
import * as Yup from "yup";
interface FormFieldsProps extends OrganizationFieldsProps {
  step: number;
  setFieldTouched?: any;
  setFieldValue?: any;
}

function RoleSelectionStep({
  setFieldValue,
  formValues,
  handleNext,
}: any): JSX.Element {
  function handleChange(e: any) {
    const { name, checked, value } = e.target;
    // setFormValues({ ...formValues, [name]: value });
    setFieldValue(name, value);
  }

  return (
    <div className="flex flex-col justify-around w-full h-full">
      <div className="">
        <RoleSelection
          id="Office Manager"
          label="Office Manager"
          value="Office Manager"
          checked={formValues?.role === "Office Manager"}
          onChange={handleChange}
          name="role"
          description="Has the capability to order and review results on behalf of associated clinicians."
        />
        <RoleSelection
          id="Physician"
          label="Clinician"
          value="Physician"
          checked={formValues?.role === "Physician"}
          onChange={handleChange}
          name="role"
          description="Can authorize orders and view results for their patients at all locations within the associated organizations."
        />
        <RoleSelection
          id="Both"
          label="Both"
          value="Both"
          checked={formValues?.role === "Both"}
          onChange={handleChange}
          name="role"
          description="Clinicians who order for themselves and view results for other clinician’s patients within the associated organizations."
        />
      </div>
    </div>
  );
}

function FormHeader({
  step,
  setStep,
}: {
  step: number;
  setStep: any;
}): JSX.Element {
  const subTitle =
    step === 1
      ? "Please enter organization details"
      : step === 2
        ? "Select Tests For Your Organization"
        : step === 3
          ? "Select role"
          : "Please enter user details";

  function handleStep() {
    if (step !== 1) {
      setStep((prevState: number) => prevState - 1);
    }
  }
  return (
    <div className="my-4">
      <Link to={"/"}>
        <LogoSVG width={112} height={38} className="my-4" />
      </Link>
      <div className="flex items-center">
        {step !== 1 && (
          <Button
            Icon={() => <DropDownIcon />}
            buttonTheme="transparent"
            onClick={handleStep}
            textInformation={{ text: "", fontWeight: "font-bold" }}
            className={"rotate-90 mr-3"}
            type="button"
          />
        )}
        <h3 className="text-title my-1.5">Sign Up</h3>
      </div>
      <h5 className="text-subTitle">{subTitle}</h5>
    </div>
  );
}

function FormFields({ step, ...props }: FormFieldsProps): JSX.Element {
  if (step === 4) {
    if (
      props.values.role.includes("Physician") ||
      props.values.role.includes("Both")
    )
      return <PhysicianFields {...props} />;
    return <OfficeManagerFields {...props} />;
  }
  if (step === 3) {
    return (
      <RoleSelectionStep
        formValues={props.values}
        setFieldValue={props.setFieldValue}
      />
    );
  }
  if (step === 2) {
    return (
      <SignUpTestSelectionStep
        setFieldValue={props.setFieldValue}
        setFieldTouched={props.setFieldTouched}
        values={props.values}
      />
    );
  }
  return <OrganizationFields {...props} />;
}

function StepIndicator({ step }: { step: number }): JSX.Element {
  return (
    <div className="flex items-center justify-center mt-4">
      {Array.from({ length: 4 }, (v, index) => (
        <div
          key={index + 1}
          className={`h-3 w-3 rounded-full ${index + 1 === step ? "bg-primary" : "bg-primaryDisabled"
            } mr-1`}
        />
      ))}
    </div>
  );
}

function FormFooter({
  step,
  onClick,
  disabled,
  submitting,
  errors,
}: {
  step: number;
  disabled: boolean;
  submitting: boolean;
  onClick: () => void;
  errors: any;
}): JSX.Element {
  const step1Valid =
    !!errors.org &&
    Object?.keys(errors?.org)?.filter((key) => key !== "cbsTests")?.length > 0;
  const step2Valid = !!errors.org && Object?.keys(errors?.org).length > 0;
  const step3Valid = !!errors.role;
  const step4Valid = !!errors.user && Object?.keys(errors?.user).length > 0;
  const buttonDisabled =
    step === 1
      ? step1Valid
      : step === 2
        ? step2Valid
        : step === 3
          ? step3Valid
          : step4Valid;

  return (
    <Fragment>
      <Button
        textInformation={{
          text: step === 4 ? "Sign up" : "next",
          fontWeight: "font-bold",
        }}
        onClick={onClick}
        type={"button"}
        buttonTheme="primary"
        className="w-full mt-4 justify-center"
        disabled={disabled || buttonDisabled}
        loading={submitting}
      />
      <StepIndicator step={step} />
    </Fragment>
  );
}

function SignUpFlowForm(): JSX.Element {
  const [step, setStep] = useState(1);
  const { mutate, isPending } = useSignUpUser();
  const { data: statesData } = useStateAndCity();

  return (
    <ErrorBoundary>
      <Formik
        initialValues={dataConstants.signUpFlowInitialValues}
        validationSchema={validations.signUpFlow.shape({
          ...validations.signUpFlow.fields,
          org: validations.organizationSignUp.shape({
            ...validations.organizationSignUp.fields,
            state: Yup.string().test(
              "is-valid-state",
              "State must be selected from the list",
              (value) => {
                const state = statesData?.find(
                  (state: any) => state.state_code === value
                );

                return !!state;
              }
            ),
          }),
        })}
        onSubmit={(data: SignUpFlowValues) => {
          mutate(data);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldTouched,
          setFieldValue,
          setFieldError,
          isSubmitting,
          submitForm,
          getFieldMeta,
          isValid,
        }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className="px-5 lg:px-[126px] flex flex-col justify-center h-full"
            >
              <FormHeader step={step} setStep={setStep} />

              <div className="overflow-auto max-h-[calc(100vh_-_260px)]">
                <FormFields
                  step={step}
                  touched={touched}
                  errors={errors}
                  handleChange={handleChange}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  getFieldMeta={getFieldMeta}
                  setFieldError={setFieldError}
                  handleBlur={handleBlur}
                  values={values}
                  isSignUpFlow
                />
              </div>
              <FormFooter
                step={step}
                disabled={isPending || Object.keys(touched).length === 0}
                submitting={isPending}
                errors={errors}
                onClick={() =>
                  step === 4
                    ? submitForm()
                    : setStep((prevState) => prevState + 1)
                }
              />
            </form>
          );
        }}
      </Formik>
    </ErrorBoundary>
  );
}

export default SignUpFlowForm;
